import { html, nothing } from "lit";
import { classifySessionCatalogKind } from "../chat/session-catalog.ts";
import { formatAgo } from "../format.ts";
import { icons } from "../icons.ts";
import type { GatewaySessionRow } from "../types.ts";

/**
 * Confirmation modal for destructive session deletion.
 *
 * Two variants:
 *   - single-row delete (`isDeleteAll === false`): one session key, one
 *     destructive confirm.
 *   - bulk delete (`isDeleteAll === true`): N sessions selected in the
 *     sidebar; the modal is a focused confirmation only — it does NOT
 *     let the operator re-select or de-select inside the modal. The
 *     sidebar already owns that surface; reopening it after a confirm
 *     click would defeat the destructive gate.
 *
 * UX spec (operator 2026-09-17):
 *   - Centered overlay with dark Jaisiu styling + blurred backdrop
 *   - Small red trash icon at the top of the card
 *   - Clear title: "Delete N chats?"
 *   - Warning text: "Are you sure you want to permanently delete these
 *     chats? This action cannot be undone."
 *   - Compact read-only list of rows (icon + title + small metadata)
 *   - No select-all / deselect-all / checkboxes / edit tools in this modal
 *   - Two buttons: Cancel (neutral) + Yes, delete (red destructive)
 *   - Destructive button visually emphasized but not oversized
 */
export type SessionsDeleteConfirmProps = {
  open: boolean;
  sessionKey: string | null;
  /** When true, render the bulk-delete confirmation. */
  isDeleteAll?: boolean;
  /**
   * Session rows to render in the bulk confirmation list. Sourced from
   * `sessionsDeleteAllConfirm.displayRows` — the catalog entries for
   * exactly the keys the modal will delete (operator's selection
   * minus the protected main session).
   */
  rows?: GatewaySessionRow[];
  /**
   * Total count of selected keys. Drives the "Delete N chats?" title.
   * MUST equal `rows.length` (the modal asserts this in dev). If you
   * pass `selectedCount=0` the destructive button is disabled.
   */
  selectedCount?: number;
  onConfirm: () => void;
  onCancel: () => void;
};

function sessionLabel(row: GatewaySessionRow): string {
  if (row.label) return row.label;
  if (row.displayName) return row.displayName;
  if (row.subject) return row.subject;
  return row.key;
}

function sessionKindLabel(row: GatewaySessionRow): string {
  // Mirror the sidebar row meta so the operator sees the same context
  // they just confirmed. Falls back to "Session" for unknown shapes.
  if (row.systemSent) return "Main";
  // JAISIU-types-sweep: this used to read `row.kind === "fork"` /
  // `"subagent"`, but `GatewaySessionRow.kind` is the GATEWAY's
  // key-classification (`classifySessionKey` in
  // `src/gateway/session-utils.ts`), which only ever emits
  // `direct | group | global | unknown` — both branches were dead.
  // `classifySessionCatalogKind` is the same helper the sidebar badges
  // with, derived from the row key, so the modal and the sidebar agree.
  const kind = classifySessionCatalogKind(row.key ?? "");
  if (kind === "main") return "Main";
  if (kind === "fork") return "Fork";
  if (kind === "subagent") return "Subagent";
  return "Session";
}

function renderReadOnlyRow(row: GatewaySessionRow) {
  const title = sessionLabel(row);
  const kind = sessionKindLabel(row);
  const relative = row.updatedAt ? formatAgo(row.updatedAt) : "";
  return html`
    <li class="sd-confirm-row">
      <span class="sd-confirm-row-icon" aria-hidden="true">${icons.messageSquare}</span>
      <span class="sd-confirm-row-info">
        <span class="sd-confirm-row-title">${title}</span>
        <span class="sd-confirm-row-meta muted"> ${kind}${relative ? ` · ${relative}` : ""} </span>
      </span>
    </li>
  `;
}

export function renderSessionsDeleteConfirm(props: SessionsDeleteConfirmProps) {
  if (!props.open) {
    return nothing;
  }

  if (props.isDeleteAll) {
    const rows = props.rows ?? [];
    // Canonical count: the actual rendered rows. We deliberately ignore
    // `props.selectedCount` if it disagrees with `rows.length` — the
    // modal trusts the rows it can render. `selectedCount` is still
    // taken for back-compat with the prop but the visible list is the
    // source of truth (UX-R-confirm-count, JAISIU-3327).
    const count = rows.length;
    // When the operator's selection is empty (or every selected key
    // resolved to the protected main session and got filtered out),
    // the destructive copy would be misleading. Render a clearer
    // empty-state copy and disable the destructive button.
    const nothingToDelete = count === 0;
    return html`
      <div
        class="exec-approval-overlay sessions-delete-overlay sessions-delete-overlay--bulk"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sessions-delete-bulk-title"
        aria-describedby="sessions-delete-bulk-desc"
      >
        <div class="exec-approval-card sessions-delete-card sessions-delete-card--bulk">
          <div class="sd-bulk-header">
            <span class="sd-bulk-icon" aria-hidden="true">${icons.trash}</span>
            <div class="sd-bulk-title-block">
              <div id="sessions-delete-bulk-title" class="sd-bulk-title">
                Delete ${count} chat${count === 1 ? "" : "s"}?
              </div>
              <div id="sessions-delete-bulk-desc" class="sd-bulk-sub">
                ${
                  nothingToDelete
                    ? "Nothing to delete — your selection was empty or the only chat you selected is the main session, which is protected."
                    : "Are you sure you want to permanently delete these chats? This action cannot be undone."
                }
              </div>
            </div>
          </div>

          ${
            rows.length > 0
              ? html`
                  <ul class="sd-confirm-list" role="list">
                    ${rows.map((row) => renderReadOnlyRow(row))}
                  </ul>
                `
              : nothing
          }

          <div class="sd-bulk-actions">
            <button type="button" class="sd-bulk-cancel" @click=${props.onCancel}>Cancel</button>
            <button
              type="button"
              class="sd-bulk-confirm"
              ?disabled=${nothingToDelete}
              @click=${props.onConfirm}
            >
              Yes, delete
            </button>
          </div>
        </div>
      </div>
    `;
  }

  const key = props.sessionKey ?? "";
  return html`
    <div
      class="exec-approval-overlay sessions-delete-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sessions-delete-title"
      aria-live="polite"
    >
      <div class="exec-approval-card sessions-delete-card">
        <div class="exec-approval-header">
          <div>
            <div id="sessions-delete-title" class="exec-approval-title">Delete session?</div>
            <div class="exec-approval-sub">
              Removes the session entry and archives its transcript
            </div>
          </div>
        </div>
        <p class="sessions-delete-key mono muted">${key}</p>
        <div class="exec-approval-actions">
          <button type="button" class="btn danger" @click=${props.onConfirm}>Delete</button>
          <button type="button" class="btn" @click=${props.onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  `;
}
